// An email or meeting VERA proposed. Nothing happens until the person presses Send / Book here AND says Yes in the
// Windows confirmation, which shows exactly who it goes to and what it says. They can change anything first.

import { useEffect, useRef, useState } from "react";
import { Icon, useStudio } from "./ui";
import { cleanError, graph, openLink } from "../lib/m365";
import type { Attachment, DocumentAction, VeraAction } from "../api/vera";
import { readFiles } from "../lib/attachments";
import { applyPdfEdits, dataUrlToBytes, type PdfEditResult } from "../lib/pdfedit";
import { fileName, FORMAT_LABEL, formatsFor, generate, MIME, type FileFormat } from "../lib/docgen";
import { openFile, saveFile } from "../lib/excel";
import { desktop } from "../lib/desktop";
import { gammaConnected, makeInGamma } from "../lib/apps";
import { vera } from "../api/vera";
import { L, T } from "../lib/i18n";

type Done = NonNullable<VeraAction["done"]>;
const list = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
const toDataUrl = (f: Attachment) => f.dataUrl ?? `data:text/plain;base64,${btoa(unescape(encodeURIComponent(f.text ?? "")))}`;
const DONE_LABEL = (): Record<Done, string> => ({ sent: L("Sent", "U dërgua"), saved: L("Saved in your Drafts", "U ruajt te draftet"), booked: L("Booked", "U caktua"), changed: T("Done"), declined: L("Dismissed", "U hodh poshtë") });

export function ActionCard({ action, files, docs, onDone }: { action: VeraAction; files?: Attachment[]; docs?: DocumentAction[]; onDone: (done: Done) => void }) {
  if (action.type === "email" && action.email) return <EmailCard action={action} files={files} docs={docs} onDone={onDone} />;
  if (action.type === "meeting" && action.meeting) return <MeetingCard action={action} onDone={onDone} />;
  if (action.type === "meeting_change" && action.meetingChange) return <MeetingChangeCard action={action} onDone={onDone} />;
  if (action.type === "document" && action.document) return <DocumentCard action={action} />;
  if (action.type === "pdfedit" && action.pdfEdit) return <PdfEditCard action={action} files={files} />;
  return null;
}

/** Changes VERA proposed inside a PDF the person attached: applied here to their original file, then checked and downloaded. */
function PdfEditCard({ action, files }: { action: VeraAction; files?: Attachment[] }) {
  const { toast } = useStudio();
  const edit = action.pdfEdit!;
  const pick = useRef<HTMLInputElement>(null);
  const isPdf = (f: Attachment) => Boolean(f.dataUrl) && (/\.pdf$/i.test(f.name) || f.mime === "application/pdf");
  const initial = files?.find((f) => f.name === edit.fileName && isPdf(f)) ?? files?.filter(isPdf).at(-1) ?? null;
  const [source, setSource] = useState<Attachment | null>(initial);
  const [out, setOut] = useState<{ bytes: Uint8Array; results: PdfEditResult[] } | null>(null);
  const [working, setWorking] = useState(false);
  const name = (edit.outputName && /\.pdf$/i.test(edit.outputName) ? edit.outputName : `${edit.fileName.replace(/\.pdf$/i, "")} (${L("edited", "ndryshuar")}).pdf`).replace(/[\\/:*?"<>|]/g, "-");
  useEffect(() => {
    if (!source?.dataUrl) return;
    setWorking(true);
    applyPdfEdits(dataUrlToBytes(source.dataUrl), edit.ops).then(setOut).catch((e) => toast(cleanError(e))).finally(() => setWorking(false));
  }, [source, edit.ops, toast]);
  async function choose(list: FileList) {
    const r = await readFiles(list);
    const pdf = r.files.find(isPdf);
    if (pdf) setSource(pdf); else toast(L("Please choose the PDF file.", "Të lutem zgjidh skedarin PDF."));
  }
  const describe = (r: PdfEditResult) => {
    const o = r.op;
    if (o.type === "replace_text") return L(`"${o.find}" → "${o.replace ?? ""}"`, `"${o.find}" → "${o.replace ?? ""}"`) + (r.ok && (r.count ?? 0) > 1 ? ` (${r.count}×)` : "");
    if (o.type === "fill_field") return L(`Field "${o.field}": ${o.value ?? ""}`, `Fusha "${o.field}": ${o.value ?? ""}`);
    if (o.type === "add_text") return L(`Text on page ${o.page ?? 1}: "${o.text}"`, `Tekst në faqen ${o.page ?? 1}: "${o.text}"`);
    if (o.type === "delete_pages") return L(`Delete page(s) ${(o.pages ?? []).join(", ")}`, `Fshi faqen/faqet ${(o.pages ?? []).join(", ")}`);
    return L(`Rotate page(s) ${(o.pages ?? []).join(", ")}`, `Rrotullo faqen/faqet ${(o.pages ?? []).join(", ")}`);
  };
  const missed = out?.results.filter((r) => !r.ok).length ?? 0;
  return (
    <Shell title={L(`Changes in ${edit.fileName}`, `Ndryshime në ${edit.fileName}`)} icon="doc">
      {edit.summary && <p className="mute" style={{ margin: "6px 0 0", fontSize: 12.5 }}>{edit.summary}</p>}
      {!source ? (
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <span className="faint" style={{ fontSize: 12.5 }}>{L("Choose the original PDF again so the changes can be made in it.", "Zgjidh përsëri PDF-në origjinale që ndryshimet të bëhen në të.")}</span>
          <button className="btn primary" onClick={() => pick.current?.click()}>{L("Choose the PDF", "Zgjidh PDF-në")}</button>
          <input ref={pick} type="file" accept="application/pdf,.pdf" hidden onChange={(x) => { if (x.target.files) void choose(x.target.files); x.target.value = ""; }} />
        </div>
      ) : working || !out ? <p className="faint" style={{ fontSize: 12.5, marginTop: 8 }}>{L("Making the changes in your PDF…", "Po bëhen ndryshimet në PDF…")}</p> : (
        <>
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12.5 }}>
            {out.results.map((r, i) => <li key={i} style={{ color: r.ok ? undefined : "var(--bad)" }}>{r.ok ? "✓ " : "✗ "}{describe(r)}{r.ok ? "" : L(" (not found in the PDF)", " (nuk u gjet në PDF)")}</li>)}
          </ul>
          {missed > 0 && <p className="faint" style={{ fontSize: 12, margin: "4px 0 0" }}>{L("Ask VERA again with the exact words as they appear in the PDF, or let her make an edited copy as a new document.", "Pyete VERA-n përsëri me fjalët ekzakte siç janë në PDF, ose kërkoji të bëjë një kopje të ndryshuar si dokument të ri.")}</p>}
          <div className="row" style={{ gap: 6, marginTop: 8 }}>
            <button className="btn primary" onClick={() => void saveFile(name, out.bytes, "application/pdf")}>{L("Download the edited PDF", "Shkarko PDF-në e ndryshuar")}</button>
            <button className="btn" onClick={() => void openFile(name, out.bytes, "application/pdf")}>{L("Open it", "Hape")}</button>
          </div>
          <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>{L("Made on this computer, in your original file. Check it before you send it. Replaced words are covered, not erased from the file, so don't use this to hide confidential information.", "U bë në këtë kompjuter, në skedarin tënd origjinal. Kontrolloje para se ta dërgosh. Fjalët e zëvendësuara mbulohen, nuk fshihen nga skedari, prandaj mos e përdor për të fshehur informacion konfidencial.")}</p>
        </>
      )}
    </Shell>
  );
}

/** A file VERA prepared. Made here, on this computer, in the format the person picks. */
function DocumentCard({ action }: { action: VeraAction }) {
  const { toast } = useStudio();
  const doc = action.document!;
  const [working, setWorking] = useState<string | null>(null);
  const formats = formatsFor(doc);
  const ordered = doc.preferredFormat && formats.includes(doc.preferredFormat) ? [doc.preferredFormat, ...formats.filter((f) => f !== doc.preferredFormat)] : formats;
  const preview = doc.kind === "document" ? doc.markdown.replace(/[#*_|`>-]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 220)
    : doc.kind === "spreadsheet" ? doc.sheets.map((s) => `${s.name}: ${s.rows.length} row${s.rows.length === 1 ? "" : "s"}, ${s.columns.length} columns`).join(" · ")
    : `${doc.slides.length + 1} slides: ${doc.slides.map((s) => s.title).slice(0, 4).join(", ")}${doc.slides.length > 4 ? "…" : ""}`;
  // With Gamma connected, any document or deck VERA made can become a Gamma presentation in the company's own account.
  const [gamma, setGamma] = useState(false);
  useEffect(() => { if (vera.mode === "live" && doc.kind !== "spreadsheet") void gammaConnected().then(setGamma); }, [doc.kind]);
  async function toGamma() {
    setWorking("gamma");
    try {
      const text = doc.kind === "document" ? doc.markdown : doc.kind === "presentation" ? [doc.subtitle ?? "", ...doc.slides.map((s) => `# ${s.title}\n${s.bullets.map((b) => `- ${b}`).join("\n")}`)].join("\n\n") : "";
      const r = await makeInGamma(text, doc.title, "presentation");
      if (r.status === "completed" && r.gammaUrl) { toast(L("Your Gamma presentation is ready. It opens in Gamma.", "Prezantimi në Gamma është gati. Po hapet në Gamma.")); openLink(r.gammaUrl); }
      else toast(r.error ?? L("Gamma couldn't finish it.", "Gamma nuk e përfundoi."));
    } catch (e) { toast(cleanError(e)); } finally { setWorking(null); }
  }
  async function act(format: FileFormat, how: "open" | "save") {
    setWorking(`${how}-${format}`);
    try {
      const name = fileName(doc.title, format), bytes = await generate(doc, format);
      if (how === "open") await openFile(name, bytes, MIME[format]);
      else { const where = await saveFile(name, bytes, MIME[format]); if (where) toast(L(`Saved ${where}`, `U ruajt ${where}`)); }
    } catch (e) { toast(e instanceof Error ? e.message : L("That file couldn't be made.", "Ky skedar nuk u krijua.")); } finally { setWorking(null); }
  }
  return (
    <Shell title={doc.title} icon={doc.kind === "spreadsheet" ? "grid" : doc.kind === "presentation" ? "board" : "doc"}>
      {preview && <p className="mute" style={{ margin: "6px 0 0", fontSize: 12.5 }}>{preview}{doc.kind === "document" && doc.markdown.length > 220 ? "…" : ""}</p>}
      <div className="row" style={{ marginTop: 8, gap: 6, flexWrap: "wrap" }}>
        {ordered.map((f, i) => (
          <span key={f} className="row" style={{ gap: 2 }}>
            <button className={`btn ${i === 0 ? "primary" : ""}`} disabled={working !== null} onClick={() => void act(f, "open")}>
              {working === `open-${f}` ? L("Opening…", "Po hapet…") : !desktop && f !== "pdf" ? L(`Download ${FORMAT_LABEL[f]}`, `Shkarko ${FORMAT_LABEL[f]}`) : L(`Open ${FORMAT_LABEL[f]}`, `Hap ${FORMAT_LABEL[f]}`)}
            </button>
            {(desktop || f === "pdf") && <button className="linkbtn" disabled={working !== null} onClick={() => void act(f, "save")} aria-label={L(`Save ${FORMAT_LABEL[f]}`, `Ruaj ${FORMAT_LABEL[f]}`)} title={L(`Save the ${FORMAT_LABEL[f]} file`, `Ruaj skedarin ${FORMAT_LABEL[f]}`)}>
              {working === `save-${f}` ? L("Saving…", "Po ruhet…") : L("Save", "Ruaj")}
            </button>}
          </span>
        ))}
        {gamma && <button className="btn" disabled={working !== null} onClick={() => void toGamma()}><Icon name="board" size={14} /> {working === "gamma" ? L("Gamma is making it… (about a minute)", "Gamma po e bën… (rreth një minutë)") : L("Make it in Gamma", "Bëje prezantim në Gamma")}</button>}
      </div>
    </Shell>
  );
}

function Shell({ title, icon, done, children }: { title: string; icon: string; done?: Done; children: React.ReactNode }) {
  return (
    <div className={`proposal action-card ${done ? "is-done" : ""}`}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <b className="row" style={{ gap: 6 }}><Icon name={icon} size={14} /> {title}</b>
        {done && <span className="faint" style={{ fontSize: 12 }}>{DONE_LABEL()[done]}</span>}
      </div>
      {children}
    </div>
  );
}

function EmailCard({ action, files, docs, onDone }: { action: VeraAction; files?: Attachment[]; docs?: DocumentAction[]; onDone: (done: Done) => void }) {
  const { toast, m365 } = useStudio();
  const e = action.email!;
  const reply = Boolean(e.replyToMessageId);
  const [to, setTo] = useState(e.to.join(", "));
  const [cc, setCc] = useState(e.cc.join(", "));
  const [subject, setSubject] = useState(e.subject);
  const [body, setBody] = useState(e.body);
  const [attach, setAttach] = useState<Attachment[]>(e.attachFiles || e.attachRefs?.length ? files ?? [] : []);
  const [working, setWorking] = useState(false);
  const locked = Boolean(action.done) || working;
  const pick = useRef<HTMLInputElement>(null);
  // Attach a file from this computer, or a document VERA made in this conversation (as Word or PDF), up to 5.
  async function addLocal(list: FileList) {
    const r = await readFiles(list, attach.length);
    setAttach((a) => [...a, ...r.files].slice(0, 5));
    if (r.skipped.length) toast(L(`Not added: ${r.skipped.join("; ")}`, `Nuk u shtuan: ${r.skipped.join("; ")}`));
  }
  async function addDoc(d: DocumentAction, format: FileFormat) {
    try {
      const bytes = await generate(d, format), name = fileName(d.title, format);
      let bin = ""; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      setAttach((a) => [...a.filter((x) => x.name !== name), { name, text: "", kind: "document" as const, mime: MIME[format], size: bytes.length, dataUrl: `data:${MIME[format]};base64,${btoa(bin)}` }].slice(0, 5));
    } catch (err) { toast(cleanError(err)); }
  }

  const args = () => ({
    to: list(to), cc: list(cc), subject, body, replyToId: e.replyToMessageId ?? undefined,
    attachments: attach.map((f) => ({ name: f.name, mimeType: f.mime ?? "application/octet-stream", dataUrl: toDataUrl(f) })),
  });
  async function run(op: "mail.send" | "mail.draft") {
    if (!m365.mail()) { toast(L("Connect Gmail or Outlook in Connections first.", "Lidh më parë Gmail ose Outlook te Lidhjet.")); return; }
    setWorking(true);
    try {
      const r = await graph<{ cancelled?: boolean; sent?: boolean; webLink?: string }>(op, args());
      if (r.cancelled) return;
      if (op === "mail.send") { onDone("sent"); toast(L("Sent.", "U dërgua.")); }
      else { onDone("saved"); toast(L("Saved in your Drafts.", "U ruajt te draftet.")); openLink(r.webLink); }
    } catch (err) { toast(cleanError(err)); } finally { setWorking(false); }
  }

  return (
    <Shell title={reply ? L("Reply", "Përgjigje") : L("New email", "Email i ri")} icon="mail" done={action.done}>
      <div className="action-fields">
        <label><span>{L("To", "Për")}</span><input value={to} onChange={(x) => setTo(x.target.value)} disabled={locked} placeholder={reply ? L("The sender", "Dërguesi") : T("name@company.com")} /></label>
        <label><span>{T("Cc")}</span><input value={cc} onChange={(x) => setCc(x.target.value)} disabled={locked} /></label>
        {!reply && <label><span>{L("Subject", "Subjekti")}</span><input value={subject} onChange={(x) => setSubject(x.target.value)} disabled={locked} /></label>}
        <textarea value={body} onChange={(x) => setBody(x.target.value)} disabled={locked} rows={Math.min(14, Math.max(5, body.split("\n").length + 1))} aria-label={L("Email text", "Teksti i emailit")} />
        {!!attach.length && (
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {attach.map((f) => <span key={f.name} className="chip">{f.name}{!locked && <button className="linkbtn" aria-label={L(`Remove ${f.name}`, `Hiqe ${f.name}`)} onClick={() => setAttach((a) => a.filter((x) => x !== f))}> ×</button>}</span>)}
          </div>
        )}
        {!locked && (
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <button className="btn sm" onClick={() => pick.current?.click()}><Icon name="clip" size={13} /> {L("Attach a file", "Bashkëngjit skedar")}</button>
            <input ref={pick} type="file" multiple hidden onChange={(x) => { if (x.target.files) void addLocal(x.target.files); x.target.value = ""; }} />
            {(docs ?? []).filter((d) => d.kind !== "spreadsheet").slice(-3).flatMap((d) => (["docx", "pdf"] as FileFormat[]).map((fmt) => (
              <button key={d.title + fmt} className="chip" onClick={() => void addDoc(d, fmt)}>📎 {d.title.slice(0, 40)} ({FORMAT_LABEL[fmt]})</button>)))}
            {(docs ?? []).filter((d) => d.kind === "spreadsheet").slice(-2).map((d) => (
              <button key={d.title + "xlsx"} className="chip" onClick={() => void addDoc(d, "xlsx")}>📎 {d.title.slice(0, 40)}{" "}{T("(Excel)")}</button>))}
          </div>
        )}
      </div>
      {!action.done && (
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          <button className="btn primary" disabled={working} onClick={() => void run("mail.send")}>{L("Send", "Dërgo")}</button>
          <button className="btn" disabled={working} onClick={() => void run("mail.draft")}>{L("Save as draft", "Ruaje si draft")}</button>
          <button className="btn" disabled={working} onClick={() => onDone("declined")}>{L("Not now", "Jo tani")}</button>
        </div>
      )}
      {!action.done && <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>{L("Nothing is sent until you press Send and confirm.", "Asgjë nuk dërgohet derisa të shtypësh Dërgo dhe ta konfirmosh.")}</p>}
    </Shell>
  );
}

/** Cancel, decline or move a meeting already in the calendar. The calendar's own confirmation shows exactly which one. */
function MeetingChangeCard({ action, onDone }: { action: VeraAction; onDone: (done: Done) => void }) {
  const { toast, m365 } = useStudio();
  const c = action.meetingChange!;
  const move = c.change === "move";
  const [start, setStart] = useState((c.start ?? "").slice(0, 16));
  const [message, setMessage] = useState(c.message ?? "");
  const [working, setWorking] = useState(false);
  const locked = Boolean(action.done) || working;

  async function go() {
    if (!m365.mail()) { toast(T("Connect Gmail or Outlook in Connections first.")); return; }
    setWorking(true);
    try {
      const r = move
        ? await graph<{ cancelled?: boolean }>("meeting.move", { eventId: c.eventId, start, minutes: c.minutes ?? undefined })
        : await graph<{ cancelled?: boolean }>("meeting.cancel", { eventId: c.eventId, comment: message });
      if (r.cancelled) return;
      onDone("changed");
      toast(T(move ? "Moved. Everyone invited gets the new time." : "Done. Everyone invited has been told."));
    } catch (err) { toast(cleanError(err)); } finally { setWorking(false); }
  }

  return (
    <Shell title={T(move ? "Move a meeting" : "Cancel a meeting")} icon="cal" done={action.done}>
      <div className="action-fields">
        {c.subject && <label><span>{L("Title", "Titulli")}</span><input value={c.subject} disabled /></label>}
        {move
          ? <label><span>{T("New time")}</span><input type="datetime-local" value={start} onChange={(x) => setStart(x.target.value)} disabled={locked} /></label>
          : <label><span>{T("Note to the others")}</span><input value={message} onChange={(x) => setMessage(x.target.value)} disabled={locked} placeholder={T("Optional")} /></label>}
      </div>
      {!action.done && (
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          <button className="btn primary" disabled={working || (move && !start)} onClick={() => void go()}>{T(move ? "Move meeting" : "Cancel meeting")}</button>
          <button className="btn" disabled={working} onClick={() => onDone("declined")}>{L("Not now", "Jo tani")}</button>
        </div>
      )}
      {!action.done && <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>{T("Nothing changes until you press the button and confirm.")}</p>}
    </Shell>
  );
}

function MeetingCard({ action, onDone }: { action: VeraAction; onDone: (done: Done) => void }) {
  const { toast, m365 } = useStudio();
  const m = action.meeting!;
  const [subject, setSubject] = useState(m.subject);
  const [start, setStart] = useState(m.start.slice(0, 16));
  const [minutes, setMinutes] = useState(m.minutes);
  const [attendees, setAttendees] = useState(m.attendees.join(", "));
  const [teams, setTeams] = useState(m.teams);
  const [working, setWorking] = useState(false);
  const locked = Boolean(action.done) || working;

  async function book() {
    if (!m365.mail()) { toast(L("Connect Gmail or Outlook in Connections first.", "Lidh më parë Gmail ose Outlook te Lidhjet.")); return; }
    setWorking(true);
    try {
      const r = await graph<{ cancelled?: boolean; webLink?: string }>("meeting.create", { subject, start, minutes, attendees: list(attendees), agenda: m.agenda ?? "", teams, location: m.location ?? "" });
      if (r.cancelled) return;
      onDone("booked");
      toast(teams ? L("Booked. The invitations are on their way.", "U caktua. Ftesat po dërgohen.") : L("Booked.", "U caktua."));
    } catch (err) { toast(cleanError(err)); } finally { setWorking(false); }
  }

  return (
    <Shell title={L("Meeting", "Takim")} icon="cal" done={action.done}>
      <div className="action-fields">
        <label><span>{L("Title", "Titulli")}</span><input value={subject} onChange={(x) => setSubject(x.target.value)} disabled={locked} /></label>
        <label><span>{L("When", "Kur")}</span><input type="datetime-local" value={start} onChange={(x) => setStart(x.target.value)} disabled={locked} /></label>
        <label><span>{L("Minutes", "Minuta")}</span><input type="number" min={5} max={480} step={5} value={minutes} onChange={(x) => setMinutes(Number(x.target.value))} disabled={locked} /></label>
        <label><span>{L("With", "Me")}</span><input value={attendees} onChange={(x) => setAttendees(x.target.value)} disabled={locked} placeholder={L("Just you", "Vetëm ti")} /></label>
        {m.location && <label><span>{L("Where", "Ku")}</span><input value={m.location} disabled /></label>}
        {m.agenda && <p className="mute" style={{ whiteSpace: "pre-wrap", margin: "4px 0 0" }}>{m.agenda}</p>}
        <label className="row" style={{ gap: 6 }}><input type="checkbox" checked={teams} onChange={(x) => setTeams(x.target.checked)} disabled={locked} style={{ width: "auto" }} />{" "}{T("Teams meeting")}</label>
      </div>
      {!action.done && (
        <div className="row" style={{ marginTop: 8, gap: 6 }}>
          <button className="btn primary" disabled={working} onClick={() => void book()}>{L("Book meeting", "Cakto takimin")}</button>
          <button className="btn" disabled={working} onClick={() => onDone("declined")}>{L("Not now", "Jo tani")}</button>
        </div>
      )}
      {!action.done && <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>{L("Nothing is booked until you press Book and confirm.", "Asgjë nuk caktohet derisa të shtypësh Cakto dhe ta konfirmosh.")}</p>}
    </Shell>
  );
}
